import type { ReactNode } from 'react';
import type { Me } from '@/lib/auth/types';
import { BuildPage } from '@/pages/build/BuildPage';
import { CredentialsPage } from '@/pages/credentials/CredentialsPage';
import { HomePage } from '@/pages/home/HomePage';
import { OperationsPage } from '@/pages/operations/OperationsPage';
import { PeoplePage } from '@/pages/people/PeoplePage';
import { ApplicationPage } from '@/pages/review/ApplicationPage';
import { ApplicationsPage } from '@/pages/review/ApplicationsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';

export type Access = 'user' | 'reviewer' | 'administrator';

export interface AppRoute {
  path: string;
  /** The navigation label. Formal, and no contractions. */
  label: string;
  element: ReactNode;
  /** Who may open it. Every route here needs a session; this narrows further. */
  access: Access;
  /** Reached from another page, not the navigation: an application's detail. */
  hidden?: boolean;
}

/**
 * Every page inside the application shell, in navigation order.
 *
 * The one place a build adds a page: the router, the navigation and the access check
 * all read this, so a page cannot exist without appearing in the navigation, or
 * appear to somebody it will refuse.
 *
 * `access` decides what the interface shows. The backend decides what is allowed,
 * independently, on every request: hiding a tab is courtesy, not control.
 */
export const APP_ROUTES: readonly AppRoute[] = [
  { path: '/home', label: 'Home', element: <HomePage />, access: 'user' },
  { path: '/applications', label: 'Applications', element: <ApplicationsPage />, access: 'reviewer' },
  {
    path: '/applications/:applicationId',
    label: 'Application',
    element: <ApplicationPage />,
    access: 'reviewer',
    hidden: true,
  },
  { path: '/people', label: 'People', element: <PeoplePage />, access: 'administrator' },
  { path: '/credentials', label: 'Credentials', element: <CredentialsPage />, access: 'administrator' },
  { path: '/settings', label: 'Settings', element: <SettingsPage />, access: 'administrator' },
  { path: '/operations', label: 'Operations', element: <OperationsPage />, access: 'administrator' },
  { path: '/build', label: 'Build', element: <BuildPage />, access: 'user' },
];

export function mayOpen(route: AppRoute, me: Me): boolean {
  if (route.access === 'reviewer') return me.canReview;
  return route.access === 'user' || me.canAdminister;
}
